<template>
  <SmDrawer v-model:visibility="localVisibility" :title="i18n.t('employeeDetails')">
    <!-- Header -->
    <template #close>
      <div class="header">
        <SmButtonClose
          data-test-id="employee-detail-drawer-close-button"
          @click="localVisibility = !localVisibility" />
      </div>
    </template>

    <!-- Drawer Content -->
    <template #default>
      <!-- Employee Header -->
      <div v-if="employee" class="employee-header">
        <SmAvatar :name="employee.name || employee.email" class="bg-gray-200" />
        <div class="employee-header-info">
          <h3>{{ employee.name || i18n.t('noName') }}</h3>
          <p class="employee-email">{{ employee.email }}</p>
          <div class="employee-status">
            <el-tag
              v-if="employee.end_date"
              type="danger"
              size="small"
              effect="plain">
              {{ i18n.t('terminated') }}
            </el-tag>
            <el-tag v-else type="success" size="small" effect="plain">
              {{ i18n.t('active') }}
            </el-tag>
          </div>
        </div>
      </div>

      <!-- Spacer -->
      <div style="height: 1.5rem" />

      <!-- Employee Information -->
      <div v-if="employee" class="employee-info-grid">
        <!-- Basic Information -->
        <div class="info-section">
          <h4 class="info-section-title">{{ i18n.t('basicInformation') }}</h4>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">{{ i18n.t('email') }}:</span>
              <span class="info-value">{{ employee.email }}</span>
            </div>
            <div v-if="employee.position" class="info-item">
              <span class="info-label">{{ i18n.t('position') }}:</span>
              <span class="info-value">{{ employee.position }}</span>
            </div>
            <div v-if="employee.supervisor_name" class="info-item">
              <span class="info-label">{{ i18n.t('supervisor') }}:</span>
              <span class="info-value">{{ employee.supervisor_name }}</span>
            </div>
            <div v-if="employee.supervisor_email" class="info-item">
              <span class="info-label">{{ i18n.t('supervisorEmail') }}:</span>
              <span class="info-value">{{ employee.supervisor_email }}</span>
            </div>
          </div>
        </div>

        <!-- Employment Dates -->
        <div class="info-section">
          <h4 class="info-section-title">{{ i18n.t('employmentDates') }}</h4>
          <div class="info-grid">
            <div v-if="employee.start_date" class="info-item">
              <span class="info-label">{{ i18n.t('hiredOn') }}:</span>
              <span class="info-value">
                {{ displayDate(new Date(employee.start_date), { dateStyle: 'long' }) }}
              </span>
            </div>
            <div v-if="employee.end_date" class="info-item">
              <span class="info-label">{{ i18n.t('terminatedOn') }}:</span>
              <span class="info-value">
                {{ displayDate(new Date(employee.end_date), { dateStyle: 'long' }) }}
              </span>
            </div>
            <div v-if="employee.last_work_day" class="info-item">
              <span class="info-label">{{ i18n.t('lastWorkDay') }}:</span>
              <span class="info-value">
                {{ displayDate(new Date(employee.last_work_day), { dateStyle: 'long' }) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Additional Information -->
        <div class="info-section">
          <h4 class="info-section-title">{{ i18n.t('additionalInformation') }}</h4>
          <div class="info-grid">
            <div v-if="employee.employee_number" class="info-item">
              <span class="info-label">{{ i18n.t('employeeNumber') }}:</span>
              <span class="info-value">{{ employee.employee_number }}</span>
            </div>
            <div v-if="employee.country_code" class="info-item">
              <span class="info-label">{{ i18n.t('countryCode') }}:</span>
              <span class="info-value">{{ employee.country_code }}</span>
            </div>
            <div v-if="getEmployeeField(employee, 'department')" class="info-item">
              <span class="info-label">{{ i18n.t('department') }}:</span>
              <span class="info-value">{{ getEmployeeField(employee, 'department') }}</span>
            </div>
            <div v-if="employee.phone" class="info-item">
              <span class="info-label">{{ i18n.t('phone') }}:</span>
              <span class="info-value">{{ employee.phone }}</span>
            </div>
          </div>
        </div>

        <!-- Cost Center Allocations -->
        <div v-if="employee.cost_center_allocations && Object.keys(employee.cost_center_allocations).length > 0" class="info-section">
          <h4 class="info-section-title">{{ i18n.t('costCenterAllocations') }}</h4>
          <div class="cost-center-grid">
            <div
              v-for="allocation in processedCostCenterAllocations"
              :key="allocation.id"
              class="cost-center-item">
              <div class="cost-center-header">
                <span class="cost-center-id">{{ allocation.id }}</span>
                <span class="cost-center-percentage">{{ allocation.percentage }}%</span>
              </div>
              <div class="cost-center-progress">
                <div class="cost-center-progress-bar">
                  <div
                    class="cost-center-progress-fill"
                    :style="{ width: allocation.percentage + '%' }"
                    :class="getProgressBarClass(allocation.percentage)">
                  </div>
                </div>
              </div>
              <div class="cost-center-footer">
                <span class="cost-center-weight">{{ allocation.weight }}</span>
                <span v-if="allocation.totalWeight > 0" class="cost-center-total">
                  (of {{ allocation.totalWeight }} total)
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Data Source Information -->
        <div class="info-section">
          <h4 class="info-section-title">{{ i18n.t('dataSourceInformation') }}</h4>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">{{ i18n.t('dataSource') }}:</span>
              <span class="info-value">{{ employee.data_source_name }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ i18n.t('remoteId') }}:</span>
              <span class="info-value">{{ employee.remote_id }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ i18n.t('created') }}:</span>
              <span class="info-value">
                {{ displayDate(new Date(employee.created), { dateStyle: 'long', timeStyle: 'short' }) }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ i18n.t('lastUpdated') }}:</span>
              <span class="info-value">
                {{ displayDate(new Date(employee.updated), { dateStyle: 'long', timeStyle: 'short' }) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </SmDrawer>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Employee } from '@/client'
  import { displayDate } from '@/utils/formatter/date'
  import SmDrawer from '@/components/sm/SmDrawer.vue'
  import SmAvatar from '@/components/sm/SmAvatar.vue'
  import SmButtonClose from '@/components/sm/SmButtonClose.vue'

  const i18n = useI18n()

  const props = defineProps<{
    employee: Employee | null
    visibility: boolean
  }>()

  const emit = defineEmits<{
    'update:visibility': [value: boolean]
  }>()

  const localVisibility = computed({
    get: () => props.visibility,
    set: (value) => emit('update:visibility', value),
  })

  // Helper function to get employee field from employee_external or application_specific
  const getEmployeeField = (employee: Employee, fieldName: string): any => {
    // Check in employee_external first
    if (employee.employee_external && employee.employee_external[fieldName]) {
      return employee.employee_external[fieldName]
    }
    // Check in application_specific
    if (employee.application_specific && employee.application_specific[fieldName]) {
      return employee.application_specific[fieldName]
    }
    return null
  }

  // Process cost center allocations for display
  const processedCostCenterAllocations = computed(() => {
    if (!props.employee?.cost_center_allocations) return []

    const allocations = props.employee.cost_center_allocations
    const entries = Object.entries(allocations)

    // Calculate total weight
    const totalWeight = entries.reduce((sum, [, weight]) => sum + weight, 0)

    // Convert to array of processed allocations
    return entries
      .map(([id, weight]) => ({
        id,
        weight,
        percentage: totalWeight > 0 ? Math.round((weight / totalWeight) * 100) : 0,
        totalWeight
      }))
      .sort((a, b) => b.weight - a.weight) // Sort by weight descending
  })

  // Get progress bar class based on percentage
  const getProgressBarClass = (percentage: number): string => {
    if (percentage >= 70) return 'progress-high'
    if (percentage >= 30) return 'progress-medium'
    return 'progress-low'
  }
</script>

<style scoped lang="scss">
  .employee-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--el-border-color-light);

    .employee-header-info {
      flex: 1;

      h3 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: var(--el-text-color-primary);
      }

      .employee-email {
        margin: 0.25rem 0;
        color: var(--el-text-color-secondary);
        font-size: 0.875rem;
      }

      .employee-status {
        margin-top: 0.5rem;
      }
    }
  }

  .employee-info-grid {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  .info-section {
    .info-section-title {
      margin: 0 0 1rem 0;
      font-size: 1rem;
      font-weight: 600;
      color: var(--el-text-color-primary);
    }

    .info-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      gap: 1rem;
    }

    .info-item {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      .info-label {
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--el-text-color-secondary);
      }

      .info-value {
        font-size: 0.875rem;
        color: var(--el-text-color-primary);
        word-break: break-word;
      }
    }
  }

  .cost-center-grid {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    background-color: var(--el-fill-color-light);
    border-radius: var(--el-border-radius-base);
    padding: 1rem;
  }

  .cost-center-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    background-color: var(--el-bg-color);
    border-radius: var(--el-border-radius-small);
    border: 1px solid var(--el-border-color-light);
  }

  .cost-center-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.875rem;

    .cost-center-id {
      font-weight: 600;
      color: var(--el-text-color-primary);
    }

    .cost-center-percentage {
      font-weight: 700;
      color: var(--el-color-primary);
    }
  }

  .cost-center-progress {
    width: 100%;
  }

  .cost-center-progress-bar {
    height: 6px;
    background-color: var(--el-fill-color-lighter);
    border-radius: 3px;
    overflow: hidden;
  }

  .cost-center-progress-fill {
    height: 100%;
    border-radius: 3px;
    transition: width 0.3s ease;

    &.progress-high {
      background-color: var(--el-color-success);
    }

    &.progress-medium {
      background-color: var(--el-color-warning);
    }

    &.progress-low {
      background-color: var(--el-color-info);
    }
  }

  .cost-center-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.75rem;

    .cost-center-weight {
      font-weight: 600;
      color: var(--el-text-color-secondary);
    }

    .cost-center-total {
      color: var(--el-text-color-placeholder);
      font-weight: 500;
    }
  }
</style>

